// useInfiniteNotifications hook - Fetch notifications with infinite scroll
// Simplified version without filtering for the notifications page

import { useInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useAuthStore } from '../store/authStore';
import type { UserNotification } from '../types/notifications';

interface NotificationsResponse {
  notifications: UserNotification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

interface UseInfiniteNotificationsOptions {
  limit?: number;
}

export function useInfiniteNotifications(options: UseInfiniteNotificationsOptions = {}) {
  const { limit = 20 } = options;
  const { isAuthenticated } = useAuthStore();

  return useInfiniteQuery<NotificationsResponse>({
    queryKey: ['notifications', 'infinite', { limit }],
    queryFn: async ({ pageParam = 0 }) => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (pageParam as number).toString(),
        read_status: 'all',
      });

      return apiClient.get<NotificationsResponse>(`/notifications?${params.toString()}`);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.pagination.hasNext) {
        return undefined;
      }
      // Calculate total items fetched so far
      const totalFetched = allPages.reduce(
        (acc, page) => acc + page.notifications.length,
        0
      );
      return totalFetched;
    },
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false, // Don't refetch on focus to preserve scroll position
    // Only fetch if authenticated
    enabled: isAuthenticated,
  });
}
